import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { SmartLinkStatsMediaItem } from 'fym-shared/src/smartlinks/stats';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../../music24/components/primitives/Panel';
import { mediaStatsMessages } from './mediaStatsMessages';
import { mediaStatsRows } from './mediaStatsRows';
import { SMLMediaBlockBox } from './SMLMediaBlockBox';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media panel; each part uses its own slice of it */
import styles from './SMLMediaPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLMediaPanelProps {
  items: SmartLinkStatsMediaItem[] | undefined;
  widgets: SerializedWidget[] | null | undefined;
  pageViews: number;
  days: number;
}

/**
 * The Media panel of the SmartLink stats (design c1): one box per Media block in page order, then
 * the removed blocks with numbers in the window, and "How we count". Nothing when the page has no
 * Media block and no numbers.
 */
const SMLMediaPanelComponent: React.FC<SMLMediaPanelProps> = ({ items, widgets, pageViews, days }) => {
  const intl = useIntl();
  const rows = useMemo(() => mediaStatsRows(items, widgets), [items, widgets]);

  if (rows.length === 0) {
    return null;
  }

  return (
    <Panel className={styles.panel} data-testid="media-panel">
      <PanelHead
        title={intl.formatMessage(mediaStatsMessages.title)}
        right={intl.formatMessage({ id: 'app.sml.stats.chart.range', defaultMessage: 'last {days} days' }, { days })}
        subtitle={intl.formatMessage(mediaStatsMessages.perBlock)}
      />
      <PanelBody>
        <div className={styles.boxes}>
          {rows.map((row) => (
            <SMLMediaBlockBox key={row.blockUUID} row={row} pageViews={pageViews} />
          ))}
        </div>
        <div className={styles.how}>
          <span className={styles.numberLabel}>{intl.formatMessage(mediaStatsMessages.howTitle)}</span>
          <p>{intl.formatMessage(mediaStatsMessages.howSeen)}</p>
          <p>{intl.formatMessage(mediaStatsMessages.howPlay)}</p>
          <p>{intl.formatMessage(mediaStatsMessages.howWatched)}</p>
          <p>{intl.formatMessage(mediaStatsMessages.howPrivacy)}</p>
        </div>
      </PanelBody>
    </Panel>
  );
};

export const SMLMediaPanel = TypedMemo(SMLMediaPanelComponent);
